import { useState, useEffect, useRef } from "react";
import TikTokCarousel from "../TikTokCarousel";
import ExpandedCaption from "./ExpandedCaption";
import TikToklogo from "../../../assets/socialglass/TikTok-logo.svg";

function TikTokComponent({
  type,
  downloaded_path,
  muted,
  url,
  creatorName,
  videoRef,
  caption,
  onManualUnmute,
  onManualMute,
  showIcon = false,
  isAutoPlay = true,
  rounded = false,
}) {
  const localRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted); // Track muted state locally
  const [isExpanded, setIsExpanded] = useState(false);
  const [showOverlay, setShowOverlay] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const vid = localRef.current;
    if (!vid) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    vid.addEventListener("play", handlePlay);
    vid.addEventListener("pause", handlePause);

    return () => {
      vid.removeEventListener("play", handlePlay);
      vid.removeEventListener("pause", handlePause);
    };
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true); // Element is in view
        } else {
          setIsVisible(false); // Element is out of view
        }
      },
      {
        threshold: 0.5, // Adjust the threshold as needed
      },
    );

    if (localRef.current) {
      observer.observe(localRef.current);
    }

    return () => {
      if (localRef.current) {
        observer.unobserve(localRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (localRef.current) {
      localRef.current.muted = muted;
      setIsMuted(muted); // Ensure internal state is updated
    }
  }, [muted]);

  const mediaSources = Array.isArray(downloaded_path)
    ? downloaded_path
    : [downloaded_path];

  const source = `https://genz-staging.feeltiptop.com/${mediaSources[0]}`;
  const ext = mediaSources[0]?.split(".").pop().toLowerCase();

  const isCarousel =
    Array.isArray(downloaded_path) &&
    downloaded_path.length > 1 &&
    downloaded_path.every((path) => {
      const ext = path.split(".").pop().toLowerCase();
      return [
        "jpg",
        "jpeg",
        "png",
        "gif",
        "webp",
        "mp4",
        "webm",
        "ogg",
      ].includes(ext);
    });

  const handlePlayPause = () => {
    const videoEl = localRef.current;

    // Check if videoEl exists and if it's a video element before calling play()
    if (videoEl && videoEl.play) {
      if (videoEl.paused) {
        videoEl.play();
        setIsPlaying(true);
      } else {
        videoEl.pause();
        setIsPlaying(false);
      }
    } else {
      console.log("Not a video element or the element is not found.");
    }
  };

  const handleMuteToggle = (e) => {
    // Prevent the click event from propagating to the parent div (which is handling play/pause)
    e.stopPropagation();

    const videoEl = localRef.current;
    if (videoEl) {
      const willBeMuted = !videoEl.muted;
      videoEl.muted = willBeMuted;
      setIsMuted(willBeMuted); // Sync local muted state
      if (willBeMuted) {
        onManualMute?.(); // Inform parent to mute all
      } else {
        onManualUnmute?.(); // Inform parent to unmute all
      }
    }
  };

  if (!url) return <p>Invalid URL</p>;
  const maxCaptionLength = 100; // Max length before truncation
  const truncatedCaption =
    caption && caption.length > maxCaptionLength
      ? caption.substring(0, maxCaptionLength) + "..."
      : caption;

  const isVideo = ["mp4", "webm", "ogg"].includes(ext);

  if (isCarousel) {
    return (
      <TikTokCarousel
        type={type}
        downloaded_path={downloaded_path}
        videoRef={(el) => {
          if (videoRef) videoRef(el);
          localRef.current = el;
        }}
        muted={muted}
        onManualMute={onManualMute}
        caption={caption}
        creatorName={creatorName}
      />
    );
  }
  return (
    <div
      className={`relative flex ${rounded && "rounded-lg"} aspect-[1/1.75] h-full w-full cursor-pointer items-center justify-center overflow-hidden bg-black`}
      onClick={handlePlayPause}
    >
      {showIcon && (
        <img
          src={TikToklogo}
          alt="TikTok Logo"
          className="absolute left-2 top-2 h-6 w-6"
        />
      )}
      {["mp4", "webm", "ogg"].includes(ext) ? (
        <>
          <video
            ref={(el) => {
              if (videoRef) videoRef(el);
              localRef.current = el;
            }}
            src={source}
            muted={muted}
            {...(isAutoPlay ? { autoPlay: true } : {})}
            loop
            controlsList="nofullscreen"
            className="h-full w-full object-cover"
            playsInline
          />
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${
              isPlaying ? "opacity-0" : "opacity-50"
            }`}
          />
        </>
      ) : ["jpg", "jpeg", "png", "gif", "webp"].includes(ext) ? (
        <img
          src={source}
          alt="TikTok media"
          className="h-full w-full object-cover"
          ref={localRef}
        />
      ) : (
        <p>Unsupported media format: {ext}</p>
      )}

      {showOverlay && (
        <div className="absolute bottom-8 left-4 right-4 z-30 text-xs text-white [text-shadow:2px_2px_4px_rgba(0,0,0,0.7)]">
          {creatorName && (
            <p className="m-0 text-left font-bold">@{creatorName}test name</p>
          )}
          {caption && (
            <div>
              {!isExpanded ? (
                <p className="m-0 text-left">
                  {truncatedCaption} eststst
                  {caption.length > maxCaptionLength && (
                    <button
                      onClick={() => setIsExpanded(true)}
                      className="ml-1 cursor-pointer text-xs font-bold underline"
                    >
                      more
                    </button>
                  )}
                </p>
              ) : (
                <ExpandedCaption
                  caption={caption}
                  onCollapse={() => setIsExpanded(false)}
                />
              )}
            </div>
          )}
        </div>
      )}

      <div
        className={`absolute inset-0 bg-black transition-opacity duration-500 ${
          isVisible || ["mp4", "webm", "ogg"].includes(ext)
            ? "opacity-0"
            : "opacity-60"
        }`}
      ></div>

      {isVideo && (
        <button
          onClick={handleMuteToggle}
          className="absolute right-2 top-2 rounded-full bg-gray-700 p-2 text-white"
        >
          {isMuted ? "🔇" : "🔊"}
        </button>
      )}
    </div>
  );
}

export default TikTokComponent;
